import React, { useEffect } from "react";
import Eyebrow from "@/components/Eyebrow";
import { FadeUp } from "@/components/Motion";
import PageHeader from "@/components/PageHeader";
import CTABlock from "@/components/CTABlock";

const needs = [
  { title: "Clean financials", body: "Books that partners, lenders, and prospective investors can read without asking for footnotes." },
  { title: "Reliable reports", body: "P&L, cash flow, and utilization reports you can forecast against, not 'best-effort' spreadsheets." },
  { title: "Visibility into performance", body: "Per-practice-area and per-attorney views so the economics of the firm are never a mystery." },
];

const GHL_SCRIPT_SRC = "https://links.usabookkeepingsolutionsllc.com/js/form_embed.js";

const checklistItems = [
  "Month-end close that takes more than 10 business days",
  "P&L that doesn't break out practice areas or partners",
  "Cash flow visibility limited to a bank balance",
  "Reports rebuilt manually in spreadsheets every quarter",
  "Accrued time and WIP that aren't on the books",
  "Tax-readiness that's a year-end scramble",
  "Decisions made on data older than 30 days",
];

export default function CorporateAttorneys() {
  useEffect(() => {
    const existing = document.querySelector(`script[src="${GHL_SCRIPT_SRC}"]`);
    if (existing) return;
    const s = document.createElement("script");
    s.src = GHL_SCRIPT_SRC;
    s.async = true;
    document.body.appendChild(s);
  }, []);

  return (
    <div data-testid="corporate-attorneys-page">
      <PageHeader
        eyebrow="Corporate & Business Attorneys"
        title={<>Clarity Behind <em className="text-[#BA1A1A] not-italic">Every Financial Decision</em>.</>}
        lead="Your firm isn't just operating, it's growing. That means you need numbers you can trust so you can make decisions without hesitation."
        image={{
          src: "https://images.pexels.com/photos/443383/pexels-photo-443383.jpeg",
          alt: "Modern office building exterior",
        }}
        meta={[
          { label: "Reporting", value: "Decision-grade" },
          { label: "Close", value: "Monthly" },
          { label: "Visibility", value: "Partner-level" },
        ]}
      />

      <section className="container-editorial py-24 md:py-32" data-testid="corporate-body">
        <div className="grid md:grid-cols-12 gap-12 mb-14">
          <div className="md:col-span-5">
            <Eyebrow>What You Need</Eyebrow>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-6">
              Growth requires real numbers.
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-6">
            <p className="text-lg text-[#0C1D36]/80 max-w-xl">
              The financial systems that got the firm to its first milestone rarely scale
              to the next one. We replace "we think" with "we know."
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-[#0C1D36]/12">
          {needs.map((n, i) => (
            <FadeUp key={n.title} className="bg-white p-10" data-testid={`corp-need-${i}`}>
              <div className="eyebrow-muted">Requirement 0{i + 1}</div>
              <h3 className="font-serif text-2xl md:text-3xl text-[#0C1D36] mt-4 leading-tight">
                {n.title}
              </h3>
              <p className="text-[#4B5563] mt-5 leading-relaxed">{n.body}</p>
              <div className="h-px w-8 bg-[#BA1A1A] mt-8" />
            </FadeUp>
          ))}
        </div>

        <p className="mt-16 font-serif italic text-2xl text-[#0C1D36]/90 max-w-xl">
          We give you the numbers you can trust, so you can move with confidence.
        </p>
      </section>

      {/* LEAD MAGNET: Financial Clarity Checklist (Corporate) */}
      <section
        className="bg-[#F3EFE9] rule-top rule-bottom"
        data-testid="corporate-checklist-section"
      >
        <div className="container-editorial py-24 md:py-32">
          <div className="grid md:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="md:col-span-5">
              <Eyebrow>Free Download</Eyebrow>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-[#0C1D36] mt-6">
                The Financial Clarity Checklist.
              </h2>
              <p className="mt-6 font-serif italic text-xl md:text-2xl text-[#0C1D36]/90 max-w-md leading-snug">
                7 signs your bookkeeping is limiting your firm's growth, and what to do about it.
              </p>

              <ul className="mt-10 space-y-3 max-w-md">
                {checklistItems.map((item, i) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-[15px] text-[#0C1D36]/85"
                  >
                    <span className="mt-2 w-1.5 h-1.5 bg-[#BA1A1A] rounded-full flex-shrink-0" />
                    <span>
                      <span className="font-mono text-xs text-[#4B5563] mr-2">
                        0{i + 1}
                      </span>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>

              <p className="mt-10 text-sm text-[#4B5563] max-w-md">
                A two-minute self-audit for corporate &amp; business attorneys. Move from
                "we think" to "we know" before your next planning conversation.
              </p>
            </div>

            <div className="md:col-span-7" data-testid="corporate-checklist-form-wrapper">
              <div className="bg-white border border-[#0C1D36]/12 p-2 md:p-3 shadow-sm">
                <iframe
                  src="https://links.usabookkeepingsolutionsllc.com/widget/form/v8lsL6wj1BIl3aK5kZcd"
                  style={{
                    width: "100%",
                    minHeight: "860px",
                    border: "none",
                    borderRadius: "8px",
                    background: "#FFFFFF",
                  }}
                  id="inline-v8lsL6wj1BIl3aK5kZcd"
                  data-layout="{'id':'INLINE'}"
                  data-trigger-type="alwaysShow"
                  data-trigger-value=""
                  data-activation-type="alwaysActivated"
                  data-activation-value=""
                  data-deactivation-type="neverDeactivate"
                  data-deactivation-value=""
                  data-form-name="Corporate Checklist"
                  data-height="829"
                  data-layout-iframe-id="inline-v8lsL6wj1BIl3aK5kZcd"
                  data-form-id="v8lsL6wj1BIl3aK5kZcd"
                  title="Corporate Checklist"
                  data-testid="corporate-checklist-iframe"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABlock
        eyebrow="Corporate & Business"
        title="Ready for financials that support how your firm actually grows?"
      />
    </div>
  );
}
